import BaseEvent from '../base/BaseEvent'
import { FeatureUtil } from '../featureUtil'
import BaseMarkerLayer from '../layers/marker/BaseMarkerLayer'

import Length from '@turf/length'
import Area from '@turf/area'
import buffer from '@turf/buffer'

// 绘画对象
const drawInstances = []

export default class Draw extends BaseEvent {
    static MODES = {
        POINT: 'draw_point',
        POLYLINE: 'draw_line_string',
        POLYGON: 'draw_polygon',
        RECTANGLE: 'draw_rectangle',
        STATIC: 'static',
        DIRECT_SELECT: 'direct_select',
        SIMPLE_SELECT: 'simple_select'
    }

    static BufferPrefix = 'buffer-'

    /**
     * Draw绘制工具
     * @param {*} map 地图对象
     * @param {*} styles 绘制图层样式
     */
    constructor(styles = []) {
        super()

        this._map = null

        // marker 集合
        this._markerC = null

        // 初始化 Draw
        const base_options = {
            defaultMode: 'static',
            displayControlsDefault: false,
            styles
        }

        // draw对象
        this._draw = {
            onAdd(...rest) {
                // 保存实例对象
                !drawInstances.includes(this) && drawInstances.push(this)
                return this.__proto__.onAdd.call(this, ...rest)
            },
            onRemove(...rest) {
                // 移除实例对象
                drawInstances.includes(this) &&
                    drawInstances.splice(drawInstances.indexOf(this), 1)
                return this.__proto__.onRemove.call(this, ...rest)
            },
            changeMode(mode, ...rest) {
                // 同时只存在一个绘画对象
                if (mode !== 'static') {
                    drawInstances.forEach(i => {
                        i !== this &&
                            i.getMode() !== 'static' &&
                            i.changeMode('static')
                    })
                }
                return this.__proto__.changeMode.call(this, mode, ...rest)
            }
        }
        this._draw.__proto__ = new MapboxDraw(base_options)

        this._drawEndHandler = Draw.drawEndHandler.bind(this)
        this._updateHandler = Draw.updateHandler.bind(this)
    }

    setMap(map) {
        if (map) {
            this._map = map

            map.addControl(this._draw)
            map.on('draw.create', this._drawEndHandler)
            map.on('draw.update', this._updateHandler)
            this._markerC = new BaseMarkerLayer(map)
        } else if (this._map) {
            this.removeAllListeners()
            this._map.removeControl(this._draw)
            this._map.off('draw.create', this._drawEndHandler)
            this._map.off('draw.update', this._updateHandler)
            this._markerC.clear()
            this._markerC.removeAllListeners()

            // 移除的时候可能会造成的无法拖动的bug
            this._map.dragPan.enable()

            delete this._markerC
            delete this._draw
            delete this._map
            delete this._drawEndHandler
        }
    }

    /************************ event handler ************************/

    static drawEndHandler(evt) {
        // 如果是当前draw完成事件
        if (this._draw.get(evt.features[0].id)) {
            evt.target = this
            setTimeout(() => {
                // 取消连续绘制
                this.Mode = Draw.MODES.STATIC
                // 派发事件
                this.fire('drawend', evt)
            }, 100)
        }
    }

    static updateHandler(evt) {
        // 如果是当前draw完成事件
        if (this._draw.get(evt.features[0].id)) {
            evt.target = this
            setTimeout(() => {
                // 取消连续绘制
                this.Mode = Draw.MODES.STATIC
                // 派发事件
                this.fire('update', evt)
            }, 100)
        }
    }

    /************************ event handler ************************/

    /**
     * 原对象
     */
    get Target() {
        return this._draw
    }

    /**
     * marker集合对象
     */
    get MarkerCollection() {
        return this._markerC
    }

    /**
     * @api
     * 获取模式
     */
    get Mode() {
        const mode = this._draw.getMode()
        for (const key in Draw.MODES) {
            const element = Draw.MODES[key]
            if (element === mode) {
                return key
            }
        }
        return mode
    }

    /**
     * @api
     * 修改模式
     * @param {point|polyline|polygon|rectangle} type
     */
    set Mode(type = 'static') {
        const mode = Draw.MODES[type.toUpperCase()] || Draw.MODES.STATIC
        this._draw.changeMode(mode)
    }

    selectFeature(feature) {
        this._draw.changeMode('direct_select', { featureId: feature.id })
    }

    /**
     * @api
     * 清空绘制
     */
    clearAll() {
        this._draw.deleteAll()
        this._markerC.clear()
    }

    /**
     * @api
     * 添加
     * @param {*} feature
     */
    add(feature, { measure, bufferFeature } = {}) {
        this._draw.add(feature)
        if (measure) {
            this.addMeasureMarker(feature)
        }
        if (bufferFeature) {
            this.addBufferFeature2(feature, bufferFeature)
        }
    }

    /**
     * @api
     * 删除
     * @param {String} id
     */
    remove(id) {
        this._draw.delete(id)
        this._draw.delete(Draw.BufferPrefix + id)
        this._markerC.remove(['fid', id])
    }

    /**
     * @api
     * 根据id查询
     * @param {String} id
     */
    get(id) {
        const feature = this._draw.get(id)
        const markers = this._markerC.query(['fid', id])
        const bufferFeature = this._draw.get(Draw.BufferPrefix + id)
        return { feature, markers, bufferFeature }
    }

    /**
     * @api
     * 添加运算标签（手动调用）
     * @param {Feature} feature
     */
    addMeasureMarker(feature) {
        const type = feature.geometry.type
        if (type === 'Point') {
            return
        }

        const id = feature.id

        const { x, y } = FeatureUtil.getCoordinateAtIndex(feature, -1)
        const { x: x2, y: y2 } = FeatureUtil.getCoordinateAtIndex(feature, -2)

        let offset = [0, 0]

        // 信息marker
        const div = document.createElement('div')
        div.classList.add('measure-marker')

        // 删除按钮
        const closeSpan = document.createElement('span')
        closeSpan.classList.add('measure-marker-close')
        closeSpan.addEventListener('click', () => {
            this.remove(id)
        })

        // 测量信息
        const messageLabel = document.createElement('label')
        messageLabel.classList.add('measure-marker-message')

        if (type === 'LineString') {
            // 长度和单位
            let length = Length(feature, { units: 'meters' })
            let unit = '米'
            if (length >= 1000) {
                length = (length / 1000).toFixed(2)
                unit = '公里'
            } else {
                length = length.toFixed(2)
            }
            // 设置html
            messageLabel.innerHTML = `总长：<span>${length}</span> ${unit}`
        } else {
            let area = Area(feature)
            let unit = '平方米'
            if (area >= 1000000) {
                area = (area / 1000000).toFixed(2)
                unit = '平方千米'
            } else {
                area = area.toFixed(2)
            }
            messageLabel.innerHTML = `总面积：<span>${area}</span> ${unit}`
        }

        // marker相对位置
        if (x >= x2) {
            offset = [10, 0]
        } else {
            offset = [-10, 0]
        }
        if (y >= y2) {
            messageLabel.classList.add('top')
        } else {
            messageLabel.classList.add('bottom')
        }

        div.appendChild(closeSpan)
        div.appendChild(messageLabel)

        const marker = new mapboxgl.Marker(div, { offset }).setLngLat([x, y])
        marker.fid = id
        marker.type = 'measure'
        this._markerC.add(marker)
    }

    /**
     * 添加marker
     * @param {*} feature
     * @param {*} marker
     */
    addMarker(feature, marker) {
        if (!marker.getLngLat()) {
            const center = FeatureUtil.getCenter(feature)
            marker.setLngLat(center)
        }
        marker.fid = feature.id
        this._markerC.add(marker)
    }

    /**
     * @api
     * 添加缓冲图形（手动调用）
     * @param {Feature} feature
     * @param {Number} radius
     */
    addBufferFeature(feature, radius) {
        const bufferFeature = buffer(feature, radius, { units: 'meters' })
        return this.addBufferFeature2(feature, bufferFeature)
    }

    /**
     * @api
     * 添加缓冲图形（手动调用）
     * @param {Feature} feature
     * @param {Feature} bufferFeature
     */
    addBufferFeature2(feature, bufferFeature) {
        bufferFeature.id = Draw.BufferPrefix + feature.id
        this._draw.delete(bufferFeature.id)
        this._draw.add(bufferFeature)
        return bufferFeature
    }
}
